import React from 'react';
import useMedicalProfiles from '../../../../hooks/api/useMedicalProfiles';
import getName from '../../../../utils/getName';
import { getIntl } from '../../../../utils/intl';
import { NoteDestination } from '../utils/types';
import { Select } from '../../../v2BaseComponents/Inputs';
import { SelectOption } from '../../../v2BaseComponents/Inputs/Select/types';
import SelectLoader from './SelectLoader';

const intl = getIntl('profile-select', {
    selectProfile: 'Select Profile',
    optional: '(optional)',
    changeRestricted: 'You cannot change the profile after initial profile assignment',
});

export type ProfileSelectOwnProps = {
    value: NoteDestination;
    onChange: (value: NoteDestination) => void;
    isDisabled?: boolean;
    changeDisabled: boolean;
};

export type ProfileSelectProps = ProfileSelectOwnProps;

const ProfileSelect: React.FC<ProfileSelectProps> = ({ value, onChange, isDisabled, changeDisabled }) => {
    const { isLoadingProfiles, medicalProfiles, collaboratorMedicalProfiles } = useMedicalProfiles();
    const allMedicalProfiles = [...medicalProfiles, ...collaboratorMedicalProfiles];

    const options: SelectOption[] = allMedicalProfiles
        .map((profile) => ({
            ...profile,
            label: getName(profile),
            value: profile.medicalProfileId,
            testID: getName(profile),
        }))
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
        .map((profile) => ({
            label: profile.label || '',
            value: profile.value || '',
            testID: profile.testID || '',
        }));
    const validatedValue =
        options.find((option) => 'medicalProfileId' in value && option.value === value.medicalProfileId) || null;

    if (isLoadingProfiles) {
        return <SelectLoader />;
    }

    return (
        <Select
            isDisabled={isDisabled || changeDisabled}
            isRequired
            title={`${intl('selectProfile')}${changeDisabled ? ` ${intl('optional')}` : ''}`}
            selectOptions={options}
            value={validatedValue?.value || null}
            onChange={(selectValue) => {
                onChange(
                    selectValue
                        ? {
                              ...value,
                              entityType: 'medical-profile',
                              medicalProfileId: selectValue,
                          }
                        : {
                              entityType: 'user',
                          },
                );
            }}
            testID="note-transfer-profile-select"
        />
    );
};

export default React.memo(ProfileSelect);
